import { useEffect, useRef, useCallback } from "react";
import { CURRENT_USER } from 'src/constants';

const ACTIVITY_EVENTS = ["mousemove", "keydown", "mousedown", "touchstart", "scroll"];
const LAST_ACTIVITY_KEY = "lastActivityAt";

export function useSessionManager({ onInactivityExpired, onSessionExpired, pathname }) {
    const inactivityTimer = useRef(null);

    const resetInactivityTimer = useCallback((inactivityTimeoutMs) => {
        clearTimeout(inactivityTimer.current);
        inactivityTimer.current = setTimeout(() => {
            onInactivityExpired();
        }, inactivityTimeoutMs);
    }, [onInactivityExpired]);

    useEffect(() => {
        // ── ne rien faire sur la page login ──
        if (pathname == "/") {
            clearTimeout(inactivityTimer.current);
            return;
        }
        const currentUser = JSON.parse(localStorage.getItem(CURRENT_USER));
        if (currentUser == null || currentUser == undefined) return;
        const sessionStart = parseInt(localStorage.getItem("sessionStart"));
        const sessionDurationMinutes = currentUser.expiration;
        const inactivityTimeoutMinutes = currentUser.inactivityTimeoutMinutes;

        if (!sessionStart || !sessionDurationMinutes || !inactivityTimeoutMinutes) return;

        const inactivityTimeoutMs = inactivityTimeoutMinutes * 60 * 1000;
        const sessionExpiresAt = sessionStart + sessionDurationMinutes * 60 * 1000;
        const remaining = sessionExpiresAt - Date.now();

        if (remaining <= 0) {
            onSessionExpired();
            return;
        }

        // Timer durée max session (commun à tous les onglets, basé sur sessionStart)
        const sessionTimer = setTimeout(() => onSessionExpired(), remaining);

        // ── Activité locale → met à jour lastActivityAt dans localStorage ──
        const handleLocalActivity = () => {
            localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
            resetInactivityTimer(inactivityTimeoutMs);
        };

        // ── Activité depuis un AUTRE onglet → événement "storage" ──
        const handleStorageEvent = (e) => {
            if (e.key === LAST_ACTIVITY_KEY) {
                // Un autre onglet a eu de l'activité → on remet notre timer à zéro
                resetInactivityTimer(inactivityTimeoutMs);
            }
        };

        // Init : on repart du dernier timestamp d'activité connu (tous onglets)
        const lastActivity = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) || Date.now());
        const elapsed = Date.now() - lastActivity;
        const remainingInactivity = inactivityTimeoutMs - elapsed;

        if (remainingInactivity <= 0) {
            onInactivityExpired();
            return;
        }

        // Démarre le timer avec le temps restant réel
        clearTimeout(inactivityTimer.current);
        inactivityTimer.current = setTimeout(() => onInactivityExpired(), remainingInactivity);

        ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, handleLocalActivity, { passive: true }));
        window.addEventListener("storage", handleStorageEvent);

        return () => {
            clearTimeout(sessionTimer);
            clearTimeout(inactivityTimer.current);
            ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, handleLocalActivity));
            window.removeEventListener("storage", handleStorageEvent);
        };
    }, [pathname, onSessionExpired, onInactivityExpired, resetInactivityTimer]);
}
